import {useMemo} from 'react';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {ApiApplication, ApplicationModel} from '../../models/api/applicationModel';
import {CompanyModel} from '../../models/api/companyModel';
import {ApiSearchApplications, SearchApplicationsModel} from '../../models/api/searchApplicationModel';
import {fetchApiAsync} from '../../services/http/http';
import {PaginationParameter} from '../helpers/resourceListPagination/resourceListPaginationOffset';
import {makeResourceListPaginationSearchOffset} from '../helpers/resourceListPagination/resourceListPaginationSearchOffset';
import {usePersistedListMutation, useResourceList} from '../helpers/resourceTableHooks';
import {useSettingsQuery} from '../helpers/useSettingsQuery';
import {useQueryDebounce} from './fetcherHelpers';
import {
  InstalledApplicationListMutation,
  installedApplicationListMutations,
  InstalledApplicationListPageType,
} from './installedApplicationsMutations';

/*
 * Consts.
 */

const installedApplicationQueryKeyFactory = {
  all: ['installedApplications'],
  list: (query: string) => [...installedApplicationQueryKeyFactory.all, {query}],
  id: (applicationId: number) => ['installedApplication', {id: applicationId}],
};

/*
 * Fetchers.
 */

export function useFetchInstalledApplications(query: string) {
  const debouncedQuery = useQueryDebounce(query);

  const queryKey = installedApplicationQueryKeyFactory.list(debouncedQuery);

  const applicationPagination = useMemo(
    () =>
      makeResourceListPaginationSearchOffset<ApplicationModel, SearchApplicationsModel>({
        apiType: ApiSearchApplications,
        makeSearchUrl: (page, company) => buildInstalledApplicationsSearchUrl(company, debouncedQuery, page),
      }),
    [debouncedQuery],
  );

  const {items, loadMore, moreStatus, hasError, showErrorBanner, refetch} = useResourceList(
    queryKey,
    applicationPagination,
  );

  const {mutate, isLoading, isSuccess} = usePersistedListMutation<
    InstalledApplicationListMutation,
    InstalledApplicationListPageType,
    typeof installedApplicationListMutations,
    Error
  >(installedApplicationQueryKeyFactory.all, installedApplicationListMutations, showErrorBanner, true);

  return {
    items,
    loadMore,
    moreStatus,
    hasError,
    mutate,
    mutateIsLoading: isLoading,
    mutateIsSuccess: isSuccess,
    refetch,
  };
}

export function useFetchInstalledApplication(applicationId: number) {
  const {data, asyncStatus, refetch} = useSettingsQuery(
    installedApplicationQueryKeyFactory.id(applicationId),
    async ({company, cancelToken}) =>
      fetchApiAsync(ApiApplication, `${company.url}/application_installations/${applicationId}`, {
        cancelToken,
      }),
  );

  return {application: data, status: asyncStatus, refetch};
}

/*
 * Helpers.
 */

function buildInstalledApplicationsSearchUrl(
  company: CompanyModel,
  query: string,
  pagination: PaginationParameter,
) {
  const path = `${company.url}/search_application_installations/${encodeURIComponent(query.toLowerCase())}`;

  return addQueryToPath(path, pagination);
}
